import AddressListPanel from 'Components/Generic/Address/AddressListPanel';
import { showSuccessToast } from 'Components/Generic/BOSToasterUtil';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import StatusToggleButton from 'Components/Generic/Buttons/StatusToggleButton';
import UserOrgRolesPanel from 'Components/Generic/Roles/UserOrgRolesPanel';
import UserRolesPanel from 'Components/Generic/Roles/UserRolesPanel';
import AppData from 'Data/AppData';
import { useAlertContext } from 'Data/context/Alerts';
import { OBJECT_STATUS, user_roles } from 'Data/Enum';
import GenericTableData from 'Data/GenericTableData';
import SiteDrawerData from 'Data/SiteDrawerData';
import UserData from 'Data/UserData';
import DataManager from 'Lib/DataManager';
import { useStateWithRef } from 'Lib/hooks/useStateWithRef';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { isUserAuthorized } from 'Util';
import UserInformationPanel from './UserInformationPanel';

let DM = new DataManager();
let UserAddressData = GenericTableData.getTableDataBus(
  'USER_ADD',
  'user_addresses',
);

const UserDetails = ({
  userId,
  userObject,
  params,
  isMyAccountPage,
  editable = false,
  ...props
}) => {
  const [user, setUser, userRef] = useStateWithRef({});
  const [addressIdList, setAddressIdList] = useState([]);

  const { openConfirmationDialog } = useAlertContext();

  useEffect(() => {
    DM.add(
      UserData.registerListener('BLANK_RECORD_LOADED', onUserLoaded),
      UserData,
    );
    DM.add(UserData.registerListener('RECORD_LOADED', onUserLoaded), UserData);
    DM.add(
      UserData.registerListener('RECORD_ACTIVATED', onUserActivated),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_DEACTIVATED', onUserDeactivated),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!userId && !params?.id && !isMyAccountPage) {
      UserData.getBlank();
    }
  }, []);

  useEffect(() => {
    let currentUserId = (isMyAccountPage && AppData.userID()) || 0;
    if (currentUserId > 0) {
      getUser(currentUserId);
    }
  }, [isMyAccountPage]);

  useEffect(() => {
    if (userId != null) {
      getUser(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (params?.id) {
      getUser(params.id);
    }
  }, [params]);

  useEffect(() => {
    if (userObject) {
      setUser(userObject);
    }
  }, [userObject]);

  useEffect(() => {
    if (user.addresses?.length) {
      let idList = user.addresses.map((add) => add.id);
      setAddressIdList(idList);
    } else {
      setAddressIdList([]);
    }
  }, [user]);
  const getUser = (id) => {
    UserData.getRecord(id, {
      _include: 'addresses',
    });
  };

  const onUserLoaded = (payload) => {
    if (payload) {
      setUser(payload);
    }
  };

  const onSaveAddress = (isNew, savedAddress) => {
    if (isNew) {
      UserAddressData.createRecord({
        address_id: savedAddress.id,
        user_id: user.id,
      });
    }
    getUser(user.id);
  };

  const deleteAddressLinks = (addressId) => {
    if (addressId) {
      UserAddressData.deleteRecordsWhere({
        address_id: addressId,
        organization_id: user.id,
      });
    }
  };

  let statusCancelButton =
    !isMyAccountPage &&
    editable &&
    (user?.id ? (
      <StatusToggleButton
        currentStatus={user.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const toggleUserStatus = () => {
    userRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? UserData.deactivateRecord(userRef.current?.id)
      : UserData.activateRecord(userRef.current?.id);
  };

  const statusToggleClicked = () => {
    let currentStatusActive =
      userRef.current?.status === OBJECT_STATUS.ACTIVE.key;
    openConfirmationDialog({
      onConfirm: toggleUserStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate User?'
        : 'Reactivate User?',
      icon: currentStatusActive ? 'disable' : undefined,
      intent: currentStatusActive ? 'danger' : 'primary',
    });
  };

  const onUserActivated = (payload) => {
    showSuccessToast('User Reactivated');
    getUser(payload.id);
  };

  const onUserDeactivated = (payload) => {
    showSuccessToast('User Deactivated');
    getUser(payload.id);
  };

  return (
    user && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <UserInformationPanel title='Details' editable={editable} {...props} />
        <AddressListPanel
          addressIdList={addressIdList}
          deleteAddressLinks={deleteAddressLinks}
          editable={editable && (!!user?.id || user.id === 0)}
          canAddAddress={editable && (!!user?.id || user.id === 0)}
          disabled={!user?.id && user.id !== 0}
          addressType='User'
          onSaveAddress={onSaveAddress}
        />
        {!isMyAccountPage &&
          isUserAuthorized({
            roles: [
              user_roles.ADMINISTRATOR,
              user_roles.PERMISSIONS_ADMINISTRATOR,
            ],
          }) && (
            <UserRolesPanel
              userId={user.id}
              editable={editable && (!!user?.id || user.id === 0)}
              disabled={!user?.id && user.id !== 0}
            />
          )}
        {!isMyAccountPage &&
          isUserAuthorized({
            roles: [
              user_roles.ADMINISTRATOR,
              user_roles.PERMISSIONS_ADMINISTRATOR,
            ],
          }) && (
            <UserOrgRolesPanel
              userId={user.id}
              editable={editable && (!!user?.id || user.id === 0)}
              disabled={!user?.id && user.id !== 0}
            />
          )}
        {statusCancelButton}
      </div>
    )
  );
};

UserDetails.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userObject: PropTypes.object,
  params: PropTypes.shape({ id: PropTypes.string }),
  isMyAccountPage: PropTypes.bool,
  editable: PropTypes.bool,
};

export default UserDetails;
